import {
  DebugTimerStartNode,
  DebugTimerEndNode,
  DebugTimerStateSchema,
} from './nodes/DebugTimerNode';
import { assertSimulationTaskHost } from './scheduleSimulationTask';
import { z } from 'zod';
import { LogicEdgeDtoSchema } from '@/schemas/logic';
import type { LogicGraph } from './LogicGraph';
import { LogicNodeInstance } from './LogicNodeInstance';
import {
  FalseNode,
  TrueNode,
  WireNode,
  NotNode,
  DiodeNode,
  LeverNode,
  ClockNode,
  DelayNode,
} from './nodes';
import { ButtonNode } from './nodes/ButtonNode';

const finite = z.number().finite();
export const NodeSchedulerCheckpointSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('clock'),
      last: finite,
      initialized: z.boolean(),
      deadline: finite.nullable(),
    })
    .strict(),
  z.object({ kind: z.literal('delay'), scheduledTime: finite.nullable() }).strict(),
]);
export type NodeSchedulerCheckpoint = z.infer<typeof NodeSchedulerCheckpointSchema>;
const taskSchema = z
  .object({
    remaining: finite.min(0),
    order: z.number().int().min(0).max(1),
    kind: z.enum(['work', 'timer', 'fallback']),
    stage: z.enum(['waiting', 'ready', 'microtask']),
  })
  .strict();
export const SimulationCheckpointSchema = z
  .object({
    version: z.literal(1),
    abi: z.literal('digitiles-native-events-1'),
    topology: z.string().max(16_000_000),
    running: z.boolean(),
    maximumSpeed: z.boolean(),
    maxSyncIterations: z.number().int().min(1).max(100_000),
    asyncDelay: finite.min(0).max(5000),
    eventTime: finite,
    wallCursor: finite,
    lastTickOffset: finite,
    tick: taskSchema.nullable(),
    eventWake: taskSchema.nullable(),
    propagationWaves: z.number().int().min(0).max(10_000),
    dirty: z.array(z.string()).max(100_000),
    events: z.array(z.object({ node: z.string(), at: finite }).strict()).max(100_000),
    edges: z.array(LogicEdgeDtoSchema).max(1_000_000),
    nodes: z
      .array(
        z
          .object({
            id: z.string(),
            type: z.string(),
            runtimeKey: z.string().optional(),
            state: z.unknown(),
            output: z.boolean(),
            elapsedOffset: finite.nullable(),
            scheduler: NodeSchedulerCheckpointSchema.nullable(),
          })
          .strict(),
      )
      .max(100_000),
  })
  .strict();
export type SimulationCheckpoint = z.infer<typeof SimulationCheckpointSchema>;

const definitions = [
  DebugTimerStartNode,
  DebugTimerEndNode,
  FalseNode,
  TrueNode,
  WireNode,
  NotNode,
  DiodeNode,
  LeverNode,
  ButtonNode,
  ClockNode,
  DelayNode,
];
// Pin native hooks. A named custom definition cannot claim this ABI.
const native = new Map(
  definitions.map((d) => [
    d.type,
    {
      definition: d,
      process: d.process,
      setup: d.setup,
      needsInputSync: d.needsInputSync,
      createDefaultState: d.createDefaultState,
    },
  ]),
);
export function assertCheckpointGraph(graph: LogicGraph) {
  if (!graph.isTopologyTracked || graph.nodes.size > 100_000)
    throw Error('Checkpoint requires bounded tracked native graph');
  for (const node of graph.nodes.values()) {
    const entry = native.get(node.type);
    if (
      !entry ||
      node.definition !== entry.definition ||
      node.definition.process !== entry.process ||
      node.definition.setup !== entry.setup ||
      node.definition.needsInputSync !== entry.needsInputSync ||
      node.definition.createDefaultState !== entry.createDefaultState ||
      node.process !== LogicNodeInstance.prototype.process
    )
      throw Error(`Checkpoint cannot resume custom definition ${node.type}`);
  }
}
export function checkpointTopology(graph: LogicGraph) {
  assertCheckpointGraph(graph);
  // Node/edge order affects native wave and downstream frontier insertion order.
  return JSON.stringify({
    nodes: [...graph.boundaryNodes()].map((n) => [
      n.idText,
      n.type,
      n.runtimeKey ?? null,
      (n.definition.configurationKeys || []).map((k) => [k, n.state[k]]),
    ]),
    edges: [...graph.edges.values()].map((e) => [
      graph.nodeIdText(e.sourceNodeId),
      graph.nodeIdText(e.targetNodeId),
    ]),
  });
}
const sourceState = z.object({ isOn: z.boolean() }).strict();
const clockState = z
  .object({
    intervalMs: finite,
    timeAccumulator: finite,
    isOn: z.boolean(),
    enabled: z.boolean().optional(),
  })
  .strict();
const delayState = z
  .object({
    delayMs: finite,
    queue: z
      .array(z.object({ value: z.boolean(), targetTime: finite }).strict())
      .max(100_000),
    currentOutput: z.boolean(),
    lastProcessTime: finite.optional(),
  })
  .strict();
export function validateSimulationCheckpoint(
  input: unknown,
  graph: LogicGraph,
): SimulationCheckpoint {
  const c = SimulationCheckpointSchema.parse(input);
  if (c.topology !== checkpointTopology(graph))
    throw Error('Checkpoint topology/configuration mismatch');
  if (
    (c.eventWake && (!c.events.length || c.eventWake.order === c.tick?.order)) ||
    (!c.running && (c.tick || c.eventWake))
  )
    throw Error('Invalid checkpoint host work');
  for (const task of [c.tick, c.eventWake])
    if (
      task &&
      ((task.kind === 'work' && task.stage === 'waiting') ||
        (task.kind === 'fallback' && task.stage !== 'waiting') ||
        (task.kind === 'timer' && task.stage === 'microtask'))
    )
      throw Error('Invalid checkpoint task stage');
    else if (task && task.stage !== 'waiting' && task.remaining !== 0)
      throw Error('Invalid ready task deadline');
    else if (task) assertSimulationTaskHost(task);
  const nodes = [...graph.nodes.values()];
  if (c.nodes.length !== nodes.length) throw Error('Incomplete checkpoint nodes');
  c.nodes.forEach((saved, i) => {
    const node = nodes[i];
    if (
      saved.id !== node.idText ||
      saved.type !== node.type ||
      saved.runtimeKey !== node.runtimeKey
    )
      throw Error('Checkpoint node identity/order mismatch');
    if (node.type === 'CLOCK') clockState.parse(saved.state);
    else if (node.type === 'DELAY') delayState.parse(saved.state);
    else if (node.type === 'LEVER' || node.type === 'BUTTON')
      sourceState.parse(saved.state);
    else if (node.type === 'DEBUG_TIMER_START' || node.type === 'DEBUG_TIMER_END') {
      DebugTimerStateSchema.strict().parse(saved.state);
      if (saved.output) throw Error('Timer nodes cannot output a signal');
    } else if (saved.state !== null) throw Error('Invalid stateless checkpoint');
    for (const key of node.definition.configurationKeys || [])
      if (!Object.is((saved.state as any)?.[key], node.state?.[key]))
        throw Error('Checkpoint state configuration mismatch');
    if (
      saved.scheduler &&
      (node.type === 'CLOCK' ? 'clock' : node.type === 'DELAY' ? 'delay' : '') !==
        saved.scheduler.kind
    )
      throw Error('Invalid node scheduler kind');
  });
  if (
    JSON.stringify(c.edges.map((e) => [e.sourceNodeId, e.targetNodeId])) !==
    JSON.stringify(
      [...graph.edges.values()].map((e) => [
        graph.nodeIdText(e.sourceNodeId),
        graph.nodeIdText(e.targetNodeId),
      ]),
    )
  )
    throw Error('Checkpoint edge order mismatch');
  const byId = new Map(c.nodes.map((n) => [n.id, n]));
  if (new Set(c.dirty).size !== c.dirty.length || c.dirty.some((id) => !byId.has(id)))
    throw Error('Invalid checkpoint frontier');
  const issued = new Set<string>();
  for (const e of c.events) {
    const scheduler = byId.get(e.node)?.scheduler;
    const at =
      scheduler?.kind === 'clock' ? scheduler.deadline : scheduler?.scheduledTime;
    if (!scheduler || issued.has(e.node) || at !== e.at)
      throw Error('Invalid checkpoint timed event');
    issued.add(e.node);
  }
  for (const n of c.nodes) {
    const at =
      n.scheduler?.kind === 'clock' ? n.scheduler.deadline : n.scheduler?.scheduledTime;
    if (at != null && !issued.has(n.id)) throw Error('Missing checkpoint timed event');
    if (n.scheduler?.kind === 'clock' && at != null) {
      const state = n.state as z.infer<typeof clockState>;
      if (
        !state.enabled ||
        state.intervalMs <= 0 ||
        at !== n.scheduler.last + Math.max(0, state.intervalMs - state.timeAccumulator)
      )
        throw Error('Inconsistent Clock issued deadline');
    }
    if (
      n.scheduler?.kind === 'delay' &&
      at != null &&
      at !== (n.state as z.infer<typeof delayState>).queue[0]?.targetTime
    )
      throw Error('Inconsistent Delay scheduled head');
  }
  return c;
}
